import React from 'react'
import { useSelector } from 'react-redux'
import { Avatar as MuiAvatar } from '@mui/material'

import type { IconName } from '~components/icons'
import Icon, { type SemanticIconName } from '~components/internal/Icon'
import { type RootState } from '~redux/rootReducer'
import { useGetIdentityQuery } from '~redux/slices/apiSlice'
import {
  type AccentColor,
  type AccentColorName,
  accentColors,
  accentPalette,
  colors,
  typography,
} from '~tokens/internal'
import { type Identity0, type Identity1, type Identity2, type IdentityId } from '~types'

import Typography from '../Typography'

import './Avatar.scss'

// Size configurations - mapped to typography.fontSize tokens
const sizeConfig = {
  xs: {
    size: typography.fontSize.md,
    iconSize: typography.fontSize.micro,
  },
  sm: {
    size: typography.fontSize.xl,
    iconSize: typography.fontSize.xs,
  },
  md: {
    size: typography.fontSize.xxl,
    iconSize: typography.fontSize.lg,
  },
  lg: {
    size: typography.fontSize.giga,
    iconSize: typography.fontSize.huge,
  },
  xl: {
    size: typography.fontSize.tera,
    iconSize: typography.fontSize.mega,
  },
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type AvatarColor = AccentColorName

interface AvatarProps {
  size?: AvatarSize | number | string
  src?: string | undefined
  alt?: string
  backgroundColor?: string | undefined
  identity?: Identity0 | Identity1 | Identity2
  identityId?: IdentityId
  ariaHidden?: boolean
  color?: AvatarColor
  colorIndex?: number
  variant?: 'default' | 'bright' | 'square'
  iconName?: SemanticIconName | IconName | React.ReactNode
  iconSize?: number | string
  iconFill?: 1 | 0
  iconColor?: string
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>
  children?: React.ReactNode
  showStaffIcon?: boolean
  className?: string
  style?: React.CSSProperties
}

const isNumericString = (value: string): boolean => /^\d+$/.test(value)

const getInitial = (name: string): string => {
  const trimmed = name.trim()
  return trimmed.charAt(0).toUpperCase()
}

const getDisplayLabel = (name: string): string => {
  const trimmed = name.trim()
  return isNumericString(trimmed) ? trimmed.slice(0, 2) : getInitial(trimmed)
}

const toPx = (value: string): number => {
  if (value.endsWith('rem')) return Math.round(parseFloat(value) * 16)
  return parseInt(value, 10) || 32
}

const resolveSize = (
  size?: AvatarSize | number | string,
): { px: string; iconSize: string; initialSize: string } => {
  if (!size) {
    const pxVal = toPx(sizeConfig.md.size)
    return {
      px: sizeConfig.md.size,
      iconSize: sizeConfig.md.iconSize,
      initialSize: `${Math.round(pxVal * 0.55)}px`,
    }
  }
  if (typeof size === 'number') {
    return {
      px: `${size}px`,
      iconSize: `${Math.round(size * 0.625)}px`,
      initialSize: `${Math.round(size * 0.55)}px`,
    }
  }
  if (size in sizeConfig) {
    const cfg = sizeConfig[size as AvatarSize]
    const pxVal = toPx(cfg.size)
    return {
      px: cfg.size,
      iconSize: cfg.iconSize,
      initialSize: `${Math.round(pxVal * 0.55)}px`,
    }
  }
  const pxVal = toPx(size)
  return {
    px: size.endsWith('rem') ? size : `${pxVal}px`,
    iconSize: `${Math.round(pxVal * 0.625)}px`,
    initialSize: `${Math.round(pxVal * 0.55)}px`,
  }
}

const getAvatarColor = (
  isStaff: boolean,
  color: AvatarColor | undefined,
  colorIndex?: number,
  name?: string,
  isBright?: boolean,
): { bg: string; fg: string; bd: string; icon500: string } => {
  let palette: AccentColor | undefined
  let colorKey: keyof typeof colors | undefined

  if (colorIndex !== undefined) {
    palette = accentPalette[colorIndex % accentPalette.length]
    // Find color key from palette reference (fallback to purple)
    colorKey = (Object.keys(accentColors) as Array<keyof typeof accentColors>).find(
      (k) => accentColors[k] === palette,
    ) as keyof typeof colors | undefined
  } else if (color) {
    palette = accentColors[color]
    colorKey = color as keyof typeof colors
  } else if (name) {
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    palette = accentPalette[hash % accentPalette.length]
    colorKey = (Object.keys(accentColors) as Array<keyof typeof accentColors>).find(
      (k) => accentColors[k] === palette,
    ) as keyof typeof colors | undefined
  } else if (isBright) {
    // Bright variant defaults to neutral when no color specified
    palette = accentColors.neutral
    colorKey = 'neutral'
  } else {
    palette = accentColors.purple
    colorKey = 'purple'
  }

  // Get 500 level color for bright variant icon
  const icon500 =
    colorKey && colors[colorKey]
      ? (colors[colorKey] as typeof colors.blue)[500]
      : colors.purple[500]

  const finalPalette = palette ?? accentColors.purple

  return isStaff
    ? { bg: finalPalette.main, fg: colors.neutral[0], bd: finalPalette.main, icon500 }
    : {
        bg: isBright ? finalPalette.lighter : finalPalette.light,
        fg: finalPalette.main,
        bd: finalPalette.mediumLight,
        icon500,
      }
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size = 'md',
  backgroundColor,
  identity,
  identityId,
  ariaHidden = false,
  color,
  colorIndex,
  variant = 'default',
  iconName,
  iconSize: iconSizeProp,
  iconFill = 1,
  iconColor,
  imgProps,
  children,
  showStaffIcon = false,
  className,
  style: styleProp,
}) => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const { data: queryIdentity } = useGetIdentityQuery(
    {
      user_id: userId || '',
      identity_id: identityId,
    },
    { skip: !identityId },
  )
  const identityInfo = queryIdentity?.identity ?? identity
  const { px, iconSize: defaultIconSize, initialSize } = resolveSize(size)
  const iconSize =
    typeof iconSizeProp === 'number' ? `${iconSizeProp}px` : (iconSizeProp ?? defaultIconSize)
  const name = alt || identityInfo?.name
  const displayLabel = name ? getDisplayLabel(name) : undefined
  const isStaff = !!name
  const isBright = variant === 'bright' || variant === 'square'
  const isSquare = variant === 'square'
  const avatarColor = getAvatarColor(
    showStaffIcon ? false : isStaff,
    color,
    colorIndex,
    name,
    isBright,
  )

  const isKnownSize = typeof size === 'string' && size in sizeConfig
  const sizeClass = isKnownSize ? `internal-avatar--${size}` : undefined
  const legacySizeStyle = isKnownSize ? undefined : { width: px, height: px }
  const borderStyle = isSquare
    ? { backgroundColor: avatarColor.bg, borderRadius: '0.5rem' }
    : isBright
      ? { backgroundColor: avatarColor.icon500 }
      : undefined

  const buildClassName = (...classes: (string | undefined)[]): string =>
    [...classes, className].filter(Boolean).join(' ')

  if (src || identityInfo?.image) {
    return (
      <MuiAvatar
        src={src || identityInfo?.image || undefined}
        alt={alt || identityInfo?.name}
        aria-hidden={ariaHidden}
        className={buildClassName('internal-avatar', sizeClass)}
        style={{ ...legacySizeStyle, ...styleProp }}
        slotProps={imgProps ? { img: imgProps } : undefined}
      >
        {children}
      </MuiAvatar>
    )
  }

  if (iconName) {
    const hasExplicitColor = !!(color || colorIndex !== undefined || backgroundColor)
    const iconColorVars = hasExplicitColor
      ? ({
          '--avatar-bg': backgroundColor || avatarColor.bg,
          '--avatar-fg': isSquare
            ? avatarColor.icon500
            : isBright
              ? avatarColor.bg
              : avatarColor.fg,
        } as React.CSSProperties)
      : isSquare
        ? ({
            '--avatar-bg': avatarColor.bg,
            '--avatar-fg': avatarColor.icon500,
          } as React.CSSProperties)
        : isBright
          ? ({
              '--avatar-bg': avatarColor.icon500,
              '--avatar-fg': avatarColor.bg,
            } as React.CSSProperties)
          : undefined
    const isStringIcon = typeof iconName === 'string'
    return (
      <MuiAvatar
        alt={isStringIcon ? alt || iconName : alt}
        aria-hidden={ariaHidden}
        className={buildClassName('internal-avatar', 'internal-avatar--named-icon', sizeClass)}
        style={{ ...iconColorVars, ...legacySizeStyle, ...borderStyle }}
      >
        {isStringIcon ? (
          <Icon
            name={iconName as SemanticIconName | IconName}
            size={iconSize}
            fill={iconFill}
            color={
              hasExplicitColor || isBright
                ? isSquare
                  ? avatarColor.icon500
                  : isBright
                    ? avatarColor.bg
                    : avatarColor.fg
                : colors.neutral[700]
            }
          />
        ) : (
          <span
            className='internal-avatar__custom-icon'
            style={{ width: iconSize, height: iconSize }}
          >
            {iconName}
          </span>
        )}
      </MuiAvatar>
    )
  }

  const colorVars = {
    '--avatar-bg': backgroundColor || avatarColor.bg,
    '--avatar-fg': iconColor || avatarColor.fg,
  } as React.CSSProperties

  if (name && !showStaffIcon) {
    return (
      <MuiAvatar
        aria-hidden={ariaHidden}
        className={buildClassName('internal-avatar', 'internal-avatar--initials', sizeClass)}
        style={{ ...colorVars, ...legacySizeStyle, ...borderStyle }}
      >
        <Typography sx={{ fontSize: initialSize }}>{displayLabel}</Typography>
      </MuiAvatar>
    )
  }

  return (
    <MuiAvatar
      aria-hidden={ariaHidden}
      className={buildClassName('internal-avatar', 'internal-avatar--icon', sizeClass)}
      style={{ ...colorVars, ...legacySizeStyle, ...borderStyle }}
    >
      <Icon name='person' size={iconSize} fill={iconFill} />
    </MuiAvatar>
  )
}

export default Avatar
